import { useState, useEffect } from 'react';
import { Plus, ChevronDown, Clock, Trash2, CheckCircle, Play } from 'lucide-react';
import { FlatRateItem as ImportedFlatRateItem, markFlatRateItemsCompleted } from '../../lib/flat-rates';

import SelectFlatRateModal from '../SelectFlatRateModal';
import { supabase } from '../../lib/supabase';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import { FlatRateTemplate, getFlatRateTemplatesByService } from '../../lib/flatRateTemplates';
import { selectInputContentOnFocus } from '../../lib/utils';
import toast from 'react-hot-toast';

type BookingFlatRatesTabProps = {
  bookingId: string;
  businessId: string;
  onUpdate: () => Promise<void>;
  onError: (error: string) => void;
  booking: { 
    status: string;
    services?: Array<{
      service_id: string;
      name: string;
    }>;
  };
};

interface ExtendedFlatRateItem extends ImportedFlatRateItem {
  resource_id: string | null;
  resource_flat_rate_amount: number | null;
  resource_hourly_rate: number | null;
  resource_first_name?: string | null;
  resource_last_name?: string | null;
  custom_hours?: number;
  completed_at?: string | null;
  completed_by?: string | null;
}



interface ResourceGroup {
  id: string | null;
  groupKey: string; // Unique key for this group (resource_id + completion status)
  name: string;
  items: ExtendedFlatRateItem[];
  totalHours: number;
  totalEarnings: number;
  isCompleted: boolean;
  completedAt?: string | null;
}

function BookingFlatRatesTab({
  bookingId,
  businessId,
  onUpdate,
  onError,
  booking
}: BookingFlatRatesTabProps) {
  const [resourceGroups, setResourceGroups] = useState<ResourceGroup[]>([]);
  const [isExpanded, setIsExpanded] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [isSelectFlatRateModalOpen, setIsSelectFlatRateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [templates, setTemplates] = useState<FlatRateTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [completingResourceId, setCompletingResourceId] = useState<string | null>(null);

  const toggleGroup = (groupId: string) => {
    setIsExpanded(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const loadEntries = async () => {
    if (!supabase) {
      onError('Database connection not available');
      return;
    }

    try {
      setLoading(true);
      const { data: flatRateData, error } = await supabase
        .from('booking_flat_rates_with_details')
        .select('*')
        .eq('booking_id', bookingId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Group entries by resource and completion status to allow multiple sessions
      const entriesByGroup = (flatRateData || []).reduce<Record<string, ExtendedFlatRateItem[]>>((acc, entry) => {
        const resourceId = entry.resource_id || 'unassigned';
        const isCompleted = !!entry.completed_at;
        
        // Create separate groups for completed vs in-progress items for the same resource
        const groupKey = isCompleted 
          ? `${resourceId}_completed_${entry.completed_at}`
          : `${resourceId}_inprogress`;
        
        if (!acc[groupKey]) {
          acc[groupKey] = [];
        }
        acc[groupKey].push({
          id: entry.id,
          business_id: entry.business_id,
          name: entry.item_name,
          description: entry.item_description,
          category_id: entry.category_id,
          hours: entry.hours,
          order_index: entry.order_index,
          created_at: entry.created_at,
          updated_at: entry.updated_at,
          resource_id: entry.resource_id,
          resource_flat_rate_amount: entry.resource_flat_rate_amount,
          resource_hourly_rate: entry.resource_hourly_rate,
          resource_first_name: entry.resource_first_name,
          resource_last_name: entry.resource_last_name,
          custom_hours: entry.custom_hours,
          completed_at: entry.completed_at,
          completed_by: entry.completed_by
        });
        return acc;
      }, {});

      // Calculate totals and create resource groups
      const groups = Object.entries(entriesByGroup).map(([groupKey, items]) => {
        const resourceId = items[0].resource_id || 'unassigned';
        const totalHours = items.reduce((sum, item) => sum + (item.custom_hours ?? item.hours), 0);
        const totalEarnings = items.reduce((sum, item) => {
          const rate = item.resource_flat_rate_amount || item.resource_hourly_rate || 0;
          return sum + (rate * (item.custom_hours ?? item.hours));
        }, 0);

        // All items in a group have the same completion status
        const isCompleted = items.length > 0 && !!items[0].completed_at;
        const completedAt = isCompleted ? items[0].completed_at : null;

        // Create descriptive group name
        const baseName = resourceId === 'unassigned' ? 'Unassigned' : 
          `${items[0].resource_first_name} ${items[0].resource_last_name}`;
        
        const groupName = isCompleted 
          ? `${baseName} (Completed ${new Date(completedAt!).toLocaleDateString()})`
          : baseName;

        return {
          id: resourceId === 'unassigned' ? null : resourceId,
          groupKey,
          name: groupName,
          items,
          totalHours,
          totalEarnings,
          isCompleted,
          completedAt
        };
      }).sort((a, b) => {
        // Sort: incomplete groups first, then completed groups by completion date (newest first)
        if (a.isCompleted && !b.isCompleted) return 1;
        if (!a.isCompleted && b.isCompleted) return -1;
        if (a.isCompleted && b.isCompleted) {
          return new Date(b.completedAt!).getTime() - new Date(a.completedAt!).getTime();
        }
        return a.name.localeCompare(b.name);
      });

      setResourceGroups(groups);
      // Auto-expand if only one group
      if (groups.length === 1) {
        setIsExpanded(new Set([groups[0].groupKey]));
      }
    } catch (err) {
      console.error('Error loading flat rate entries:', err);
      onError(err instanceof Error ? err.message : 'Failed to load flat rate entries');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (booking.status === 'completed') {
      onError('Cannot delete flat rate items from completed bookings');
      return;
    }
    setItemToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete || !supabase) return;

    setDeleteLoading(true);
    try {
      // First get the booking status
      const { data: bookingData, error: bookingError } = await supabase
        .from('bookings')
        .select('status')
        .eq('id', bookingId)
        .single();

      if (bookingError) throw bookingError;

      // Delete the flat rate entry
      const { error } = await supabase
        .from('booking_flat_rates')
        .delete()
        .eq('id', itemToDelete);

      if (error) throw error;

      // If booking is completed, manually trigger the handle_flat_rate_changes function
      if (bookingData?.status === 'completed') {
        // Instead of calling the function directly, we'll update the entry to trigger the function
        const { error: updateError } = await supabase
          .from('booking_flat_rates')
          .update({ hours: 0 }) // This will trigger the handle_flat_rate_changes function
          .eq('id', itemToDelete);

        if (updateError) throw updateError;
      }

      // Refresh data
      await onUpdate();
      await loadEntries();
    } catch (err) {
      console.error('Error deleting flat rate:', err);
      onError(err instanceof Error ? err.message : 'Failed to delete flat rate');
    } finally {
      setDeleteLoading(false);
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
    }
  };

  const handleAddMultipleItems = async (
    item: ImportedFlatRateItem,
    hours: number,
    resourceId: string
  ) => {
    if (!supabase) {
      onError('Database connection not available');
      return;
    }

    try {
      const actualResourceId = resourceId === 'unassigned' ? null : resourceId;

      // Insert the new flat rate entry
      const { error } = await supabase
        .from('booking_flat_rates')
        .insert([{
          booking_id: bookingId,
          item_id: item.id,
          resource_id: actualResourceId,
          hours: hours, // Set the hours column
          custom_hours: hours // Set custom_hours to the provided hours
        }])
        .select();

      if (error) {
        console.error('Error inserting flat rate:', error);
        throw new Error(`Failed to add flat rate: ${error.message}`);
      }

      // Refresh data
      await onUpdate();
      await loadEntries();
      setIsSelectFlatRateModalOpen(false);
    } catch (err) {
      console.error('Error adding flat rates:', err);
      onError(err instanceof Error ? err.message : 'Failed to add flat rates');
    }
  };

  const handleAddClick = () => {
    setIsSelectFlatRateModalOpen(true);
  };

  const loadTemplates = async () => {
    if (!booking.services?.length) {
      setTemplates([]);
      return;
    }

    setTemplatesLoading(true);
    try {
      const allTemplates: FlatRateTemplate[] = [];
      
      // Fetch templates for each service
      for (const service of booking.services) {
        const serviceTemplates = await getFlatRateTemplatesByService(service.service_id);
        allTemplates.push(...serviceTemplates);
      }

      // Remove duplicates if a template is used in multiple services
      const uniqueTemplates = allTemplates.filter((template, index, self) =>
        index === self.findIndex((t) => t.id === template.id)
      );

      setTemplates(uniqueTemplates);
    } catch (err) {
      console.error('Error loading templates:', err);
      onError(err instanceof Error ? err.message : 'Failed to load flat rate templates');
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleMarkAsCompleted = async (resourceId: string) => {
    if (!resourceId || resourceId === 'unassigned') {
      onError('Cannot mark unassigned items as completed');
      return;
    }

    // Find the in-progress group for this resource to get the groupKey
    const inProgressGroup = resourceGroups.find(
      group => group.id === resourceId && !group.isCompleted
    );
    
    if (!inProgressGroup) {
      onError('No in-progress items found for this resource');
      return;
    }

    setCompletingResourceId(inProgressGroup.groupKey);
    try {
      await markFlatRateItemsCompleted(bookingId, resourceId);
      await loadEntries(); // Reload to show completion status
      await onUpdate(); // Update parent component
      toast.success('Flat rate items marked as completed! Payroll entries have been created.');
    } catch (err) {
      console.error('Error marking flat rate items as completed:', err);
      onError(err instanceof Error ? err.message : 'Failed to mark items as completed');
    } finally {
      setCompletingResourceId(null);
    }
  };

  useEffect(() => {
    if (bookingId) {
      loadEntries();
    }
  }, [bookingId, businessId]);

  useEffect(() => {
    if (booking.services?.length) {
      loadTemplates();
    }
  }, [booking.services]);

  return (
    <>
    <div className="space-y-4">
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm min-h-[400px] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <h3 className="text-lg font-semibold text-gray-900">Flat Rate Items</h3>
            <span className="text-sm text-gray-500">
              ({resourceGroups.reduce((total, group) => total + group.items.length, 0)})
            </span>
          </div>
          <button
            onClick={handleAddClick}
            disabled={booking.status !== 'in_progress'}
            className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
              booking.status !== 'in_progress'
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50'
            }`}
            title={booking.status !== 'in_progress' ? 'Flat rate items can only be added when booking is in progress' : undefined}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Item
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-sm text-gray-600">Loading entries...</p>
              </div>
            </div>
          ) : resourceGroups.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 space-y-4">
              <div className="text-center text-sm text-gray-500">
                No flat rate entries yet
              </div>
              <button
                onClick={handleAddClick}
                disabled={booking.status !== 'in_progress'}
                className={`flex items-center justify-center px-4 py-3 border-2 border-dashed rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${
                  booking.status !== 'in_progress'
                    ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                    : 'border-gray-300 text-gray-600 hover:text-blue-600 hover:border-blue-300 hover:bg-blue-50 focus:ring-blue-500'
                }`}
                title={booking.status !== 'in_progress' ? 'Flat rate items can only be added when booking is in progress' : undefined}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Flat Rate Item
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {resourceGroups.map((group) => (
                <div key={group.groupKey} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                  <div className="p-4">
                    {/* Header Row */}
                    <div 
                      className="flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors rounded-lg -m-1 p-1"
                      onClick={() => toggleGroup(group.groupKey)}
                    >
                      <div className="flex items-center space-x-3">
                        <ChevronDown 
                          className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
                            isExpanded.has(group.groupKey) ? 'rotate-180' : ''
                          }`}
                        />
                        <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${
                          group.isCompleted 
                            ? 'bg-green-100 text-green-600' 
                            : 'bg-blue-100 text-blue-600'
                        }`}>
                          {group.isCompleted ? (
                            <CheckCircle className="h-4 w-4" />
                          ) : (
                            <Clock className="h-4 w-4" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-bold text-gray-900">{group.name}</h3>
                            {group.isCompleted && (
                              <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                                Completed
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            {group.items.length} items • {group.totalHours} hours
                            {group.isCompleted && group.completedAt && (
                              <span className="ml-2">
                                • Completed {new Date(group.completedAt).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-sm font-semibold text-blue-600">
                        ${group.totalEarnings.toFixed(2)}
                      </div>
                    </div>
                    
                    {/* Mark as Completed Button */}
                    {!group.isCompleted && group.id && booking.status !== 'completed' && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkAsCompleted(group.id!);
                          }}
                          disabled={completingResourceId === group.groupKey}
                          className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white text-sm font-medium rounded-lg transition-colors disabled:cursor-not-allowed"
                        >
                          {completingResourceId === group.groupKey ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                              Marking Complete...
                            </>
                          ) : (
                            <>
                              <Play className="h-4 w-4" />
                              Mark as Completed
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {isExpanded.has(group.groupKey) && (
                    <div className="bg-gray-50 border-t border-gray-200">
                      {group.items.map((item, index) => (
                        <div key={item.id} className={`p-4 ${index > 0 ? 'border-t border-gray-200' : ''}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="text-sm font-bold text-gray-900">{item.name}</h4>
                              {item.description && (
                                <p className="mt-1 text-xs sm:text-sm text-gray-600">{item.description}</p>
                              )}
                              <div className="mt-2 flex items-center text-sm text-gray-500">
                                <Clock className="h-4 w-4 mr-1 text-blue-500" />
                                <input
                                  type="number"
                                  step="0.1"
                                  min="0.1"
                                  value={item.custom_hours ?? item.hours}
                                  disabled={true}
                                  onFocus={selectInputContentOnFocus}
                                  className="w-20 h-6 px-2 text-sm border border-gray-300 rounded-lg bg-gray-50 text-right cursor-not-allowed"
                                />
                                <span className="ml-1 font-medium">hours</span>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <div className="text-sm font-semibold text-blue-600">
                                ${((item.resource_flat_rate_amount || item.resource_hourly_rate || 0) * item.hours).toFixed(2)}
                              </div>
                              <button
                                onClick={() => handleDelete(item.id)}
                                disabled={booking.status === 'completed' || group.isCompleted}
                                className={`p-1.5 rounded-lg text-xs transition-all ${
                                  booking.status === 'completed' || group.isCompleted
                                    ? 'text-gray-300 cursor-not-allowed' 
                                    : 'text-red-500 hover:text-red-700 hover:bg-red-50'
                                }`}
                                title={group.isCompleted ? 'Cannot delete items from completed groups' : undefined}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm min-h-[200px] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900">Quick Add Templates</h3>
        </div>
        <div className="flex-1 p-6">
          {templatesLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-sm text-gray-600">Loading templates...</p>
              </div>
            </div>
          ) : templates.length === 0 ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-center text-sm text-gray-500">
                No templates available for this booking's services
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => handleAddClick()}
                  disabled={templatesLoading || booking.status !== 'in_progress'}
                  className={`p-3 rounded-lg border shadow-sm transition-colors text-left ${
                    templatesLoading || booking.status !== 'in_progress'
                      ? 'bg-gray-100 border-gray-200 cursor-not-allowed'
                      : 'bg-white border-gray-200 hover:bg-blue-50 hover:border-blue-300'
                  }`}
                  title={booking.status !== 'in_progress' ? 'Flat rate items can only be added when booking is in progress' : undefined}
                >
                  <div className="flex items-start space-x-2">
                    <div className="h-6 w-6 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Plus className="h-3 w-3 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-gray-900 truncate">{template.title}</h4>
                      <div className="text-sm text-gray-500 mt-1">
                        {template.hours} hours
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
    
    <SelectFlatRateModal
      isOpen={isSelectFlatRateModalOpen}
      onClose={() => setIsSelectFlatRateModalOpen(false)}
      bookingId={bookingId}
      onAdd={handleAddMultipleItems}
      selectedResourceId={null}
    />
    
    <DeleteConfirmationModal
      isOpen={isDeleteModalOpen}
      onClose={() => {
        setIsDeleteModalOpen(false);
        setItemToDelete(null);
      }}
      onConfirm={confirmDelete}
      title="Remove Flat Rate Item"
      message="Are you sure you want to remove this flat rate item? This action cannot be undone."
      loading={deleteLoading}
    />
    </>
  );
}

export default BookingFlatRatesTab;